import { ThemeEvents } from '@theme/events';
import { debounce } from '@theme/utilities';
class CollectionTopContent extends HTMLElement {
  /** @type {MutationObserver | null} */
  #observer = null;
  /** @type {() => void} */
  #debouncedSync = debounce(() => this.#syncButtonsFromFilters(), 80);
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.#syncButtonsFromFilters();
    requestAnimationFrame(() => this.#syncButtonsFromFilters());
    document.addEventListener(ThemeEvents.FilterUpdate, this.#onFilterUpdate);
    document.addEventListener('section:rendered', this.#onSectionRendered);
    // Re-sync when individual facet checkboxes toggle
    document.addEventListener('change', this.#onFacetInputChange, true);

     // Observe section morphs replacing filter markup
    try {
      const root = this.closest('.shopify-section') || document.body;
      this.#observer = new MutationObserver(() => {
        this.#debouncedSync();
      });
      this.#observer.observe(root, { childList: true, subtree: true });
    } catch {}
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#onFilterUpdate);
    document.removeEventListener('section:rendered', this.#onSectionRendered);
    document.removeEventListener('change', this.#onFacetInputChange, true);
    try { this.#observer?.disconnect(); this.#observer = null; } catch {}
  }

  /** @param {string} value */
  #cssEscape(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(value);
    return value.replace(/"/g, '\\"');
  }

  /**
   * @param {HTMLElement} btn
   * @param {boolean} active
   */
  #setBtnState(btn, active) {
    if (active) {
      btn.classList.add('btn1');
      btn.classList.remove('btn2');
    } else {
      btn.classList.add('btn2');
      btn.classList.remove('btn1');
    }
  }

  #syncButtonsFromFilters() {
    const buttons = this.querySelectorAll('.qf-btn');
    let anyActive = false;
    buttons.forEach((btn) => {
      if (!(btn instanceof HTMLElement)) return;
      const text = (btn.dataset.filter || btn.textContent || '').trim();
      if (!text) return;
      const esc = this.#cssEscape(text);
      /** @type {NodeListOf<HTMLInputElement>} */
      const checkboxes = document.querySelectorAll(`.coll-filter-list[data-label="${esc}"] input[type="checkbox"]`);
      // Hide button if no corresponding facet checkbox exists
      if (checkboxes.length === 0) {
        btn.classList.add('hidden');
        // Also ensure inactive state
        this.#setBtnState(btn, false);
        if (btn instanceof HTMLButtonElement) {
          btn.disabled = true;
          btn.toggleAttribute('aria-disabled', true);
        }
        return; // Skip further processing for this button
      } else {
        btn.classList.remove('hidden');
      }
      const anyChecked = Array.from(checkboxes).some((c) => c.checked);
      // Disable quick filter button if all matching checkboxes are disabled
      const allDisabled = checkboxes.length > 0 && Array.from(checkboxes).every((c) => c.disabled);
      if (btn instanceof HTMLButtonElement) {
        btn.disabled = allDisabled;
        btn.toggleAttribute('aria-disabled', allDisabled);
      }
      if (anyChecked) anyActive = true;
      this.#setBtnState(btn, anyChecked);
    });

    if(!anyActive) {
      const qfAllBtn = this.querySelector('.qf-all');
      if(qfAllBtn instanceof HTMLElement) {
        this.#setBtnState(qfAllBtn, true);
      }
    }
  }

  /** @param {Event} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('button[data-filter]');
    if (!(button instanceof HTMLElement) || !this.contains(button)) return;

    const value = button.dataset.filter || '';
    if (!value) return;

    if (value === '__ALL__') {
      const clearEl = document.querySelector('.facets__clear-all-link');
      if (clearEl instanceof HTMLElement) clearEl.click();
      // After clearing, reset all quick-filter buttons to inactive
      const buttons = this.querySelectorAll('.qf-btn');
      buttons.forEach((b) => {
        if (b instanceof HTMLElement) this.#setBtnState(b, false);
      });

      return;
    }

    const qfAllBtn = this.querySelector('.qf-all');
    if(qfAllBtn instanceof HTMLElement) {
      qfAllBtn.classList.remove('btn1');
      qfAllBtn.classList.add('btn2');
    }
    button.classList.remove('btn2');
    button.classList.add('btn1');

    const selector = `.coll-filter-list[data-label="${value}"] label`;
    const target = document.querySelector(selector);
    if (target instanceof HTMLElement) target.click();
  };

  // Re-sync when filters change (before section morph)
  #onFilterUpdate = () => {
    // Let the DOM settle a tick in case inputs toggle
    requestAnimationFrame(() => this.#syncButtonsFromFilters());
  };

  // Re-sync after section finishes rendering
  #onSectionRendered = () => {
    requestAnimationFrame(() => this.#syncButtonsFromFilters());
  };

  /** @param {Event} event */
  #onFacetInputChange = (event) => {
    const t = event.target;
    if (!(t instanceof HTMLInputElement)) return;
    if (!t.closest('.coll-filter-list')) return;
    // Update quickly to reflect the new checked state
    requestAnimationFrame(() => this.#syncButtonsFromFilters());
  };
}

if (!customElements.get('collection-top-content')) {
  customElements.define('collection-top-content', CollectionTopContent);
}
